import FormData from "form-data";

import { deserialize } from "../base.serializer";
import { normalizePrivateFileUploadResponse } from "../../helpers/private-file-url";

const EXT_BY_MIME = {
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/png": "png",
  "image/gif": "gif",
  "image/webp": "webp",
  "video/mp4": "mp4",
  "video/quicktime": "mov",
  "application/pdf": "pdf"
};

/**
 * Returns a safe filename for a Blob/File.
 * When the browser converts a data-URI to Blob via fetch().blob(), it loses the
 * original filename and the multipart part gets filename="blob". This derives a
 * real name from the MIME type so the backend stores the correct extension.
 */
function resolveUploadFilename(file, fallbackBase = "upload") {
  const existingName = file?.name;
  if (existingName && existingName !== "blob") return existingName;
  const ext = EXT_BY_MIME[file?.type];
  return ext ? `${fallbackBase}.${ext}` : fallbackBase;
}

/**
 * Private-bucket upload config per tool.
 * @type {Record<string, { endpoint: string, toolIdFormAttr: string }>}
 */
export const PRIVATE_FILE_TOOL_CONFIG = {
  survey: {
    endpoint: "survey_files",
    toolIdFormAttr: "survey_tool_id"
  },
  map: {
    endpoint: "survey_files",
    toolIdFormAttr: "map_tool_id"
  },
  brainstormer: {
    endpoint: "idea_files",
    toolIdFormAttr: "brainstormer_id"
  }
};

/**
 * Uploads a file to the private-bucket API for survey, map (places), or brainstormer.
 * @param {object} [options]
 * @param {"survey"|"map"|"brainstormer"} [options.tool="survey"]
 * @param {string|null} [options.recaptchaToken]
 * @param {string|null} [options.recaptchaVersion]
 * For legacy public uploads use fileUpload.
 */
export const privateBucketFileUpload = async (
  token,
  baseUrl,
  file,
  projectId,
  toolId,
  { tool = "survey", recaptchaToken = null, recaptchaVersion = null } = {}
) => {
  if (!file) {
    throw new Error("Missing required argument: file");
  }
  if (!projectId || !toolId) {
    throw new Error("Missing required argument: projectId or tool id");
  }

  const toolConfig = PRIVATE_FILE_TOOL_CONFIG[tool];
  if (!toolConfig) {
    throw new Error(
      `Invalid private file tool: ${tool}. Use one of: ${Object.keys(
        PRIVATE_FILE_TOOL_CONFIG
      ).join(", ")}`
    );
  }

  const { endpoint, toolIdFormAttr } = toolConfig;
  const queryPath = `${baseUrl}/api/v2/${endpoint}`;

  const formData = new FormData();
  formData.append("data[attributes][file]", file, resolveUploadFilename(file));
  formData.append("data[attributes][project_id]", String(projectId));
  formData.append(`data[attributes][${toolIdFormAttr}]`, String(toolId));
  if (recaptchaToken) {
    formData.append("data[attributes][g_recaptcha_response]", recaptchaToken);
    if (recaptchaVersion) {
      formData.append("data[attributes][recaptcha_version]", recaptchaVersion);
    }
  }
  const res = await fetch(queryPath, {
    method: "post",
    headers: {
      Accept: "application/json",
      ...(token && { "x-api-token": `Bearer ${token}` })
    },
    body: formData,
    credentials: "same-origin"
  });

  const json = await res.json();

  if (!res.ok) {
    const detail = json?.errors?.[0]?.detail;
    throw new Error(detail || `Private file upload failed (${res.status})`);
  }

  const data = await deserialize(json);
  return normalizePrivateFileUploadResponse(data, { tool });
};
